import { DashboardVariation, QueueDefinition } from './types';
import { CARE_OPTIONS_FOR_KIDS_QUEUES } from './variations/careOptionsForKids';
import { ELEANOR_QUEUES } from './variations/eleanor';
import { HOME_HEALTH_QUEUES } from './variations/homeHealth';
import { HOSPICE_DEMO_QUEUES } from './variations/hospiceDemo';
import { NORTH_STAR_QUEUES, NORTH_STAR_UAT_QUEUES } from './variations/northStar';
import { PENN_QUEUES } from './variations/penn';
import { PINNACLE_QUEUES, PINNACLE_UAT_QUEUES } from './variations/pinnacle';
import { TELADOC_QUEUES } from './variations/teladoc';

/**
 * A variation that renders no work queues at all, by design.
 *
 * Distinct from an empty array, which would be a claim that the org has queues and
 * none of them matched. A recorder can treat this as "nothing to record" and be right;
 * it cannot treat an unrecognised variation that way, because an hour with no rows
 * reads in a report exactly like an hour where an org genuinely had no work.
 */
export const NO_DASHBOARD = 'NO_DASHBOARD';

/**
 * Every variation the backend can report, and what it publishes.
 *
 * Exhaustive on purpose. A variation added to the enum is a compile error here until
 * someone decides whether it carries queues or none, rather than resolving to
 * `undefined` and being discovered later. "Not extracted yet" is not a state this can
 * hold: a variation the package has never heard of is one `queuesForVariation` throws
 * for, which is the safe default and needs no declaration.
 */
export const definitionsByVariation: Record<
    DashboardVariation,
    QueueDefinition[] | typeof NO_DASHBOARD
> = {
    [DashboardVariation.ADVENT_HEALTH]: NO_DASHBOARD,
    [DashboardVariation.CARE_OPTIONS_FOR_KIDS]: CARE_OPTIONS_FOR_KIDS_QUEUES,
    [DashboardVariation.ELEANOR_HEALTH]: ELEANOR_QUEUES,
    [DashboardVariation.HOME_HEALTH_DEMO]: HOME_HEALTH_QUEUES,
    [DashboardVariation.HOSPICE_DEMO]: HOSPICE_DEMO_QUEUES,
    [DashboardVariation.JET_HEALTH]: NO_DASHBOARD,
    [DashboardVariation.NOBLE_HEALTHCARE]: NO_DASHBOARD,
    [DashboardVariation.NORTH_STAR]: NORTH_STAR_QUEUES,
    [DashboardVariation.NORTH_STAR_UAT]: NORTH_STAR_UAT_QUEUES,
    [DashboardVariation.PENN_MEDICINE]: PENN_QUEUES,
    [DashboardVariation.PINNACLE_HEALTH]: PINNACLE_QUEUES,
    [DashboardVariation.PINNACLE_HEALTH_UAT]: PINNACLE_UAT_QUEUES,
    [DashboardVariation.TELADOC]: TELADOC_QUEUES,
};

/**
 * Variation entries that actually carry queues.
 *
 * The sweeps iterate this rather than the whole registry, because a variation with no
 * queues would otherwise produce an empty suite, which vitest treats as a failure.
 */
export const variationsWithQueues = (): [string, QueueDefinition[]][] =>
    Object.entries(definitionsByVariation).filter(
        (entry): entry is [string, QueueDefinition[]] =>
            entry[1] !== NO_DASHBOARD && entry[1].length > 0,
    );

/** Whether a string names a variation this package knows about. */
export const isKnownVariation = (variation: string): variation is DashboardVariation =>
    variation in definitionsByVariation;

/**
 * The queues for a variation, minus any the caller has disabled.
 *
 * Throws on a variation the package does not recognise, rather than returning nothing.
 * A run that records nothing is indistinguishable in the report from an org that had no
 * work, so an unrecognised org has to fail loudly instead.
 */
export const queuesForVariation = (
    variation: string,
    { disabledKeys = [] }: { disabledKeys?: string[] } = {},
): QueueDefinition[] => {
    if (!isKnownVariation(variation)) {
        throw new Error(`No queue definitions for dashboard variation ${variation}`);
    }
    const published = definitionsByVariation[variation];
    if (published === NO_DASHBOARD) {
        return [];
    }
    const disabled = new Set(disabledKeys);
    return published.filter((queue) => !disabled.has(queue.key));
};
